import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import CommonTable, { CommonTableProps } from "../../components/CommonTable";
import {
  addRoom,
  deleteRoom,
  fetchRoom,
  updateRoom,
} from "redux/actions/room";
import { fetchShops } from "redux/actions/shop";
import RoomRes from "../../types/res/room/RoomRes";
import CreateRoomReq from "../../types/req/room/CreateRoomReq";
import UpdateRoomReq from "../../types/req/room/UpdateRoomReq";
import { FormType } from "components/FormField";

const Room: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const rooms = useSelector((state) => state.room);
  const shops = useSelector((state) => state.shop);

  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchRoom(companyId));
  }, [companyId, dispatch]);

  const headers: { key: keyof RoomRes; label: string }[] = [
    { key: "shopId", label: t("room.headers.shopId") },
    { key: "name", label: t("room.headers.name") },
    { key: "note", label: t("room.headers.note") },
    { key: "sort", label: t("room.headers.sort") },
    { key: "isActive", label: t("room.headers.isActive") },
  ];

  const forms = [
    {
      label: t("room.forms.shopId"),
      key: "shopId",
      type: FormType.Select,
      options:
        shops?.map((s) => ({ id: String(s.shopId), name: s.name })) || [],
      // MUI Select の value 比較で number vs string mismatch を回避するため
      // defaultValues.shopId (number) を string に変換して formData.shopId にセット
      value: (val: any) => String(val),
    },
    {
      label: t("room.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("room.forms.note"),
      key: "note",
      type: FormType.TextArea,
    },
    {
      label: t("room.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
    {
      label: t("room.forms.isActive"),
      key: "isActive",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("room.options.inactive") },
        { id: "1", name: t("room.options.active") },
      ],
    },
  ];

  return (
    <RoomTable
      title={t("room.title")}
      formId="roomForm"
      forms={forms}
      rows={headers}
      data={rooms}
      addType={CreateRoomReq}
      updateType={UpdateRoomReq}
      addFunc={(formData: any) => async (d: any) => {
        return await addRoom(companyId, formData)(d);
      }}
      updateFunc={(formData: any) => async (d: any) => {
        return await updateRoom(companyId, formData)(d);
      }}
      deleteFunc={(item: any) => async (d: any) => {
        return await deleteRoom(companyId, { roomId: item.roomId })(d);
      }}
      values={[
        (s) => s.shop?.name || t("room.values.unset"),
        (s) => s.name,
        (s) => s.note || "",
        (s) => s.sort || t("room.values.unset"),
        (s) => (s.isActive ? t("room.values.active") : t("room.values.inactive")),
      ]}
    />
  );
};

const RoomTable = styled<
  React.FC<CommonTableProps<RoomRes, CreateRoomReq, UpdateRoomReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Room;
